/**
 * Tree Node Resolver
 *
 * Resolves logical tree node paths to actual node IDs by traversing
 * the simulation's Information[] tree structure.
 */

import { Information } from "../../simulation/types"
import { TreeNodeMapping } from "../types/tutorial-config"

/**
 * Resolves a path of node names to the corresponding node ID.
 *
 * @param categories - The root-level Information[] tree from simulation data
 * @param path - Array of node text values to traverse (e.g., ["Operations", "Customer Support"])
 * @returns The node ID if found, null otherwise
 *
 * @example
 * const nodeId = resolveTreeNodeId(categories, ["Businesses", "Donut Shop", "Communicate"])
 * // Returns the ID of the "Communicate" node under "Donut Shop"
 */
export function resolveTreeNodeId(
  categories: Information[],
  path: string[]
): number | null {
  if (!categories || categories.length === 0 || path.length === 0) {
    return null
  }

  const [current, ...rest] = path

  // Find node matching current path segment (case-insensitive, partial match)
  const node = categories.find((c) => {
    const nodeText = c.text.toLowerCase()
    const searchText = current.toLowerCase()
    return nodeText === searchText || nodeText.includes(searchText)
  })

  if (!node) {
    return null
  }

  // If this is the last segment, return the node ID
  if (rest.length === 0) {
    return node.id
  }

  // Continue traversing with the remaining path
  return resolveTreeNodeId(node.categories || [], rest)
}

/**
 * Builds a map of logical names to resolved tree-item selectors.
 *
 * @param categories - The root-level Information[] tree from simulation data
 * @param mappings - Array of TreeNodeMapping definitions from config
 * @returns Map of logical names to CSS selectors (e.g., { "communicateNode": "#tree-item-48860" })
 */
export function buildTreeNodeSelectorMap(
  categories: Information[],
  mappings: TreeNodeMapping[]
): Record<string, string> {
  const selectorMap: Record<string, string> = {}

  for (const mapping of mappings) {
    const nodeId = resolveTreeNodeId(categories, mapping.nodePath)
    if (nodeId !== null) {
      const suffix = mapping.suffix || ""
      selectorMap[mapping.name] = `#tree-item-${nodeId}${suffix}`
    } else {
      console.warn(
        `[TutorialConfig] Could not resolve tree node path: ${mapping.nodePath.join(" > ")}`
      )
    }
  }

  return selectorMap
}

/**
 * Resolves a selector string that may contain tree node references.
 * Tree node references use the format {{nodeName}} where nodeName
 * is a key in the selectorMap.
 *
 * @param selector - CSS selector potentially containing {{nodeName}} references
 * @param selectorMap - Map of logical names to resolved selectors
 * @returns The resolved selector string
 */
export function resolveSelector(
  selector: string,
  selectorMap: Record<string, string>
): string {
  return selector.replace(/\{\{(\w+)\}\}/g, (match, name) => {
    return selectorMap[name] || match
  })
}
